// MerchantOrdersScreen/index.jsx

import React, { useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useModalManager } from "../../hooks/useModalManager";
import { useScrollToTop } from "../../hooks/userScrollToTop";
import { useMerchant } from "../../context/merchant";
import { useTimer } from "../../hooks/useTimer";
import OrderTable from "./orderTable";
import PreorderTable from "./preorderTable"; // ✅ NEW
import MerchantBanners from "./banners";
import MerchantPerformance from "./performance";
import OrderDetailsModal from "../../modals/order-details";
import EmptyStateComponent from "../../components/empty";
import dayjs from "dayjs";

export default function MerchantOrdersScreen() {
  const { isFullScreen, toggleFullScreen, orders, ordersLoading, pollOrders } =
    useMerchant();

  const { closeModal, currentModal, openModal } = useModalManager();
  const { showScrollButton, scrollToTop } = useScrollToTop();
  const [order, setOrder] = useState({});
  const isEmpty = !ordersLoading && orders.length === 0;

  const timer = useTimer();

  useEffect(() => {
    pollOrders();
  }, []);

  const getNext7Days = () =>
    Array.from({ length: 7 }).map((_, i) => {
      const d = dayjs().add(i, "day");

      return {
        key: d.format("YYYY-MM-DD"),
        label: i === 0 ? "Өнөөдөр" : d.format("dddd"),
        date: d,
      };
    });

  const days = getNext7Days();
  const [activeDay, setActiveDay] = useState(days[0].key);

  const instantOrders = useMemo(
    () => (Array.isArray(orders) ? orders.filter((o) => !o?.isPreorder) : []),
    [orders]
  );

  const preorderOrders = useMemo(
    () => (Array.isArray(orders) ? orders.filter((o) => !!o?.isPreorder) : []),
    [orders]
  );

  const isInstantEmpty = !ordersLoading && instantOrders.length === 0;
  const isPreorderEmpty = !ordersLoading && preorderOrders.length === 0;

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          {!isFullScreen && (
            <div className="page-title-box">
              <div className="page-title-right">
                <form className="d-flex">
                  <button
                    type="button"
                    className="btn btn-primary ms-2"
                    onClick={pollOrders}
                  >
                    <i className="mdi mdi-autorenew" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary ms-1"
                    onClick={toggleFullScreen}
                    data-bs-toggle="tooltip"
                    data-bs-placement="bottom"
                    data-bs-title="Tooltip on bottom"
                  >
                    <i className="ri ri-fullscreen-line" />
                  </button>
                </form>
              </div>
              <h4 className="page-title">Шинэ захиалга</h4>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-12">
          {isFullScreen && (
            <div className="fab">
              <div
                className="btn btn-primary btn-lg rounded-start-0 mb-2"
                onClick={pollOrders}
              >
                <i className="mdi mdi-autorenew" />
              </div>
              <div
                className="btn btn-primary btn-lg rounded-start-0 mb-2"
                onClick={toggleFullScreen}
              >
                <i className="ri ri-fullscreen-exit-line" />
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-body pt-1">
              {isEmpty ? (
                <EmptyStateComponent title="Захиалга байхгүй байна" />
              ) : (
                <>
                  <ul className="nav nav-tabs nav-bordered mb-2" role="tablist">
                    <li className="nav-item" role="presentation">
                      <a
                        className="nav-link active"
                        id="tab-instant"
                        data-bs-toggle="tab"
                        href="#pane-instant"
                        role="tab"
                        aria-controls="pane-instant"
                        aria-selected="true"
                      >
                        Шинэ захиалга{" "}
                        {!ordersLoading && (
                          <span className="text-muted">
                            ({instantOrders.length})
                          </span>
                        )}
                      </a>
                    </li>

                    <li className="nav-item" role="presentation">
                      <a
                        className="nav-link"
                        id="tab-preorder"
                        data-bs-toggle="tab"
                        href="#pane-preorder"
                        role="tab"
                        aria-controls="pane-preorder"
                        aria-selected="false"
                      >
                        Урьдчилсан захиалга{" "}
                        {!ordersLoading && (
                          <span className="text-muted">
                            ({preorderOrders.length})
                          </span>
                        )}
                      </a>
                    </li>
                  </ul>

                  <div className="tab-content">
                    <div
                      className="tab-pane show active"
                      id="pane-instant"
                      role="tabpanel"
                      aria-labelledby="tab-instant"
                    >
                      <div className="table-responsive-sm">
                        {isInstantEmpty ? (
                          <EmptyStateComponent title="Шинэ захиалга байхгүй" />
                        ) : (
                          <OrderTable
                            orders={instantOrders}
                            loading={ordersLoading}
                            onRowClick={(item) => {
                              openModal("order-details");
                              setOrder(item);
                            }}
                          />
                        )}
                      </div>
                    </div>

                    <div
                      className="tab-pane"
                      id="pane-preorder"
                      role="tabpanel"
                      aria-labelledby="tab-preorder"
                    >
                      <div className="table-responsive-sm">
                        {isPreorderEmpty ? (
                          <EmptyStateComponent title="Урьдчилсан захиалга байхгүй" />
                        ) : (
                          // ✅ Preorder үед өөр table
                          <PreorderTable
                            orders={preorderOrders}
                            loading={ordersLoading}
                            onRowClick={(item) => {
                              openModal("order-details");
                              setOrder(item);
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {!isFullScreen && (
        <div className="row">
          <div className="col-12 col-xxl-7">
            <MerchantPerformance />
          </div>
          <div className="col-12 col-xxl-5">
            <MerchantBanners />
          </div>
        </div>
      )}

      <OrderDetailsModal
        show={currentModal === "order-details"}
        handleClose={closeModal}
        orderId={order.id || {}}
      />

      {showScrollButton && (
        <Button
          variant="primary"
          onClick={scrollToTop}
          className="scroll-to-top-btn"
          style={{
            position: "fixed",
            bottom: "40px",
            right: "40px",
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            padding: "0",
            zIndex: 1000,
          }}
        >
          <i className="mdi mdi-arrow-up" style={{ fontSize: "1.5rem" }} />
        </Button>
      )}
    </div>
  );
}
